import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { offlineDataManager } from '@shared/lib/offlineData';
import { Folder } from '@shared/types';

const FOLDERS_KEY = ['folders'] as const;

// Offline-first read: folder names/order are pulled into local storage during
// manual sync (useOfflineSync). Creating/renaming/deleting folders stays
// online-only, but each mutation also updates the local store so the offline
// view remains consistent between syncs.
export const useFoldersQuery = () => {
  return useQuery({
    queryKey: FOLDERS_KEY,
    queryFn: async () => offlineDataManager.getFolders(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    networkMode: 'always',
    retry: false,
  });
};

export const useCreateFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; description?: string }) => api.createFolder(payload),
    onSuccess: (folder) => {
      offlineDataManager.upsertFolder(folder);
      queryClient.setQueryData(FOLDERS_KEY, (old: Folder[] | undefined) =>
        old ? [...old, folder] : [folder]
      );
    },
  });
};

export const useUpdateFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, ...payload }: { slug: string; name?: string; description?: string }) =>
      api.updateFolder(slug, payload),
    onSuccess: (updated) => {
      offlineDataManager.upsertFolder(updated);
      queryClient.setQueryData(FOLDERS_KEY, (old: Folder[] | undefined) =>
        old?.map(f => f.folder_id === updated.folder_id ? updated : f) ?? [updated]
      );
    },
  });
};

export const useDeleteFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.deleteFolder(slug),
    onSuccess: (_, slug) => {
      offlineDataManager.deleteFolder(slug);
      queryClient.setQueryData(FOLDERS_KEY, (old: Folder[] | undefined) =>
        old?.filter(f => f.slug !== slug) ?? []
      );
    },
  });
};
